/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable max-lines-per-function */
import { useDispatch } from 'react-redux';
import { Button, ButtonGroup, Stack, Text, useColorModeValue } from '@chakra-ui/react';
import { ComponentDataIn, Components, Panel, ReferenceMap, Size, Vertex2Data } from '@rta/rta-blueprint-library';
import { DisplayMode } from '@rta/rta-blueprint-component';
import { useEffect, useState, type JSX } from 'react';

import { ContextPanelContent } from './context-panel-content';
import { lang } from '@lang';
import { popContextPanel } from '@state';
import { resolvePanelItemGroup } from '@helpers';
import { useDesign } from '@hooks';
import { InlineNumberInput, StackedStringInput } from './controls';

/**
 * Triggers auto-distribution on a panel if SKU layer is active
 * @param panel The panel to distribute items on
 * @param controller The controller to check SKU layer status
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const triggerAutoDistributionIfSkuLayerActive = (panel: Panel | null, controller: any): void => {
	if (!panel) return;

	const currentDisplayMode = controller?.navigation?.displayMode;
	let shouldDistribute = false;

	switch (currentDisplayMode) {
		case DisplayMode.SkuOverhead:
		case DisplayMode.SkuOverheadManufacturing:
			shouldDistribute = true;
			break;
		default:
			shouldDistribute = false;
			break;
	}

	if (shouldDistribute) {
		// Use setTimeout to ensure the item is fully added before distributing
		setTimeout(() => {
			panel.distributeApplianceGroupsHorizontally();
		}, 0);
	}
};

type AddCutoutPanelProps = {
	id: string | null;
	position?: Vertex2Data;
	countertopCutout?: boolean;
};

type CutoutShape = 'rectangular' | 'circular';

export const AddCutoutPanel = (props: AddCutoutPanelProps): JSX.Element => {
	const { id, position = { x: 0, y: 0 }, countertopCutout = false } = props;

	const dispatch = useDispatch();
	const [controller] = useDesign();

	const kitchen = controller?.kitchen ?? null;

	const [refMap, setRefMap] = useState<ReferenceMap | null>(null);
	const [shape, setShape] = useState<CutoutShape>('rectangular');
	const [size, setSize] = useState<Size>(countertopCutout ? { width: 12, height: 2, depth: 12 } : { width: 12, height: 12, depth: 3 });
	const [diameter, setDiameter] = useState<number>(3.5);
	const [label, setLabel] = useState<string>('');
	const [msrp, setMsrp] = useState<number>(0);

	const labelColor = useColorModeValue('gray.600', 'gray.300');

	useEffect(() => {
		if (kitchen === null || id === null) return;

		// get the reference map for the provided id
		const map = kitchen?.getReferenceMap(id);
		if (map && map.id === id) setRefMap(map);
	}, [kitchen, id]);

	if (!kitchen || !refMap || !refMap.panel) return <></>;

	const title = lang.contextPanels.title.addCutout;

	const handleShapeChange = (newShape: CutoutShape): void => {
		setShape(newShape);
	};

	return <ContextPanelContent title={title}>
		<Stack spacing="12px" width="100%" pt="10px">
			<Text pl="5px" color={labelColor} fontSize="medium" fontWeight="medium">Shape</Text>
			<ButtonGroup size="sm" isAttached width="100%">
				<Button
					flex={1}
					colorScheme={shape === 'rectangular' ? 'blue' : 'gray'}
					variant={shape === 'rectangular' ? 'solid' : 'outline'}
					onClick={() => handleShapeChange('rectangular')}
				>Rectangular</Button>
				<Button
					flex={1}
					colorScheme={shape === 'circular' ? 'blue' : 'gray'}
					variant={shape === 'circular' ? 'solid' : 'outline'}
					onClick={() => handleShapeChange('circular')}
				>Circular</Button>
			</ButtonGroup>
			<StackedStringInput
				label="Label"
				placeholder="Label or SKU..."
				value={label}
				onChange={(e) => setLabel(e.target.value)}
			/>
			<InlineNumberInput
				category="MSRP"
				label="MSRP"
				min={0}
				value={msrp}
				onChange={(e) => { setMsrp(e); }}
			/>
			{shape === 'circular' ? (
				<InlineNumberInput
					label="Diameter" min={0.5}
					step={0.125}
					value={diameter}
					onChange={(valueString) => {
						setDiameter(Number(valueString));
					}}
				/>
			) : (
				<>
					<InlineNumberInput
						label="Width" min={0.5}
						value={size.width}
						onChange={(valueString) => {
							setSize({ ...size, width: Number(valueString) });
						}}
					/>
					<InlineNumberInput
						label={countertopCutout ? 'Depth' : 'Height'} min={0.5}
						value={countertopCutout ? size.depth : size.height}
						onChange={(valueString) => {
							setSize(countertopCutout ? { ...size, depth: Number(valueString) } : { ...size, height: Number(valueString) });
						}}
					/>
				</>
			)}
			<Button
				onClick={() => {
					const isCircular = shape === 'circular';
					const categoryKey = isCircular ? 'hole' : 'cutout';
					const category = kitchen.context.categories.find((c) => c.key === categoryKey);
					const placement = countertopCutout ? Components.PLACEMENT_TOP_ONLY : Components.PLACEMENT_FRONT_ONLY;

					const cutoutSize: Size = isCircular
						? { width: diameter, height: diameter, depth: diameter }
						: size;
					const distanceBelowCountertop = countertopCutout ? cutoutSize.height : null;

					const componentData: ComponentDataIn = {
						description: label,
						size: cutoutSize,
						cutout: cutoutSize,
						sku: 'CUTOUT',
						label,
						displayName: label,
						categoryId: category?.id,
						placement,
						msrp,
						distanceBelowCountertop,
						skuColorPreset: 'Orange',
						...(isCircular ? { svg: 'gasolineCutout' as unknown as null } : {})
					};

					const posX = refMap?.panel?.isBackPanel ? -position.x : position.x;
					const targetGroup = resolvePanelItemGroup({ refMap, placement, fallbackX: posX });

					if (targetGroup) {
						targetGroup.items.new({ item: { override: true, component: componentData }});
						if (targetGroup === refMap.panelItemGroup) targetGroup.centerItemsVertically();
						// Auto-distribute if SKU layer is active
						triggerAutoDistributionIfSkuLayerActive(refMap.panel, controller);
					}
					// Close Drawer
					dispatch(popContextPanel());
				}}
			>Insert</Button>
		</Stack>
	</ContextPanelContent>;
};
